import throttle from 'lodash/throttle';
import i18next from 'i18next';
import { buffers, eventChannel, END } from 'redux-saga';
import { call, select } from 'redux-saga/effects';
import { getConfigKey } from '@wix/da-redux-config/pkg/selectors';
import {
  isApiError,
  configurePuppyOptionsAndIncludeAuthFromRedux,
  getApiResponse,
} from '@wix/da-http-client';
import { UploadError, UploadErrorCode, UploadType } from '../types';

const THROTTLE_WAIT = 500;

// Simulate file upload channel events
let fakeDeviationId = 123;
export function createFakeUploadFileChannel(uploadType: UploadType) {
  return eventChannel(emitter => {
    const throttledEmitter = throttle(emitter, THROTTLE_WAIT);
    let progress = 0;
    const interval = setInterval(() => {
      progress = Math.min(1, progress);

      throttledEmitter({ progress });

      if (progress < 1) {
        return (progress += Math.random() / 100);
      }

      if (window.doError) {
        emitter({ error: new Error('Test error') });
        emitter(END);
        return;
      }

      const response =
        uploadType === UploadType.DOCUMENT
          ? {
              deviationId: fakeDeviationId++,
              narfDocument: 'http://example.com/fake/doc/url',
              sprfDocument: 'http://example.com/fake/doc/url',
            }
          : { data: { privateId: fakeDeviationId++ } };
      emitter({ progress: 1 });
      emitter({ success: true, response });
      emitter(END);
    }, 50);

    return () => {
      clearInterval(interval);
    };
  });
}

export function getUploadErrorMessage({
  errorCode,
  errorDescription,
}: {
  errorCode?: number;
  errorDescription?: string;
}) {
  switch (errorCode) {
    case UploadErrorCode.UPLOADS_TEMPORARILY_DISABLED:
      return i18next.t('upload.error.temporarilyDisabled');
    case UploadErrorCode.ITEM_COUNT_QUOTA_LIMIT_REACHED:
      return i18next.t('upload.error.reachedFileQuota');
    default:
      if (typeof errorDescription === 'string' && errorDescription.trim()) {
        return errorDescription;
      }

      return i18next.t('uploadZone.error');
  }
}

export function* createUploadChannel(
  url: string,
  // We need a FormData object so that axios uses the form-multipart serializer
  formData: FormData,
  uploadType: UploadType
) {
  if (process.env.NODE_ENV !== 'production') {
    const doFake = yield select(getConfigKey, 'fakeApiRequests');
    if (doFake) {
      return createFakeUploadFileChannel(uploadType);
    }
  }

  const abortController = new AbortController();
  const options = yield call(
    configurePuppyOptionsAndIncludeAuthFromRedux,
    {
      url,
      method: 'post',
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      withCredentials: true,
      signal: abortController.signal,
    },
    'dashared'
  );

  for (const key in options.data) {
    formData.append(key, options.data[key]);
  }

  return eventChannel(
    emitter => {
      const throttledEmitter = throttle(emitter, THROTTLE_WAIT);

      getApiResponse({
        ...options,
        data: formData,
        onUploadProgress: (e: ProgressEvent) => {
          if (e.lengthComputable) {
            const progress = e.loaded / e.total;
            throttledEmitter({ progress });
          }
        },
      }).then(response => {
        if (!response || isApiError(response)) {
          const { errorCode, errorDescription } = response ?? {};
          const errorMessage = getUploadErrorMessage({ errorCode, errorDescription });

          const error: UploadError = new Error(errorMessage, {
            cause: errorDescription,
          });
          emitter({ error });
          emitter(END);
          return;
        }

        emitter({ success: true, response });
      });

      return () => {
        abortController.abort();
      };
    },
    buffers.sliding(2) as any
  );
}
